import { Button, Form, Input } from "antd";
import { useCallback, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useHistory, useLocation } from "react-router-dom";
import { useAppDispatch, useAppSelector } from "redux/hooks";
import { fetchEASStudents, setSearchName } from "redux/reducers/EASSlice";
import { AppURL } from "utils/Actions";
import styles from "./style.module.scss";

interface SearchByNameProps {
  desktop?: boolean;
  activeTab?: string;
  redirect?: boolean;
}

export default function SearchByName({
  desktop = false,
  redirect = false,
  activeTab
}: SearchByNameProps) {
  const history = useHistory();
  const location = useLocation();
  const { t } = useTranslation();
  const [form] = Form.useForm();
  const { isLoading, searchName } = useAppSelector(state => state.eas);
  const dispatch = useAppDispatch();
  const classNames = [styles.container];
  if (desktop) {
    classNames.push(styles["container--desktop"]);
  }

  useEffect(() => {
    if (form) {
      if (searchName) {
        form.setFieldsValue(searchName);
      } else {
        form.resetFields();
      }
    }
  }, [form, searchName]);

  const handleOnFinish = useCallback(formData => {
    if (redirect) {
      if (location.pathname !== AppURL.school.easApplications) {
        dispatch(setSearchName(formData));
        const historyObject = {
          pathname: AppURL.school.easApplications,
          state: { activeTab }
        };
        history.push(historyObject);
      }
    }
    const params = {
      firstName: formData.firstName?.toUpperCase().trim(),
      lastName: formData.familyName?.toUpperCase().trim()
    };
    dispatch(fetchEASStudents(params));
  }, []);

  const handleChange = (_: any, allFields: any[]) => {
    const nameObj = { firstName: allFields[0].value, familyName: allFields[1].value };
    dispatch(setSearchName(nameObj));
  };

  return (
    <div className={classNames.join(" ")}>
      {redirect && <div style={{ marginBottom: "2rem" }}>{t("dashboard.card-4.desc")}</div>}
      <Form
        form={form}
        layout="vertical"
        onFinish={handleOnFinish}
        requiredMark={false}
        style={{ width: "100%" }}
        className={styles.form}
        disabled={isLoading}
        onFieldsChange={handleChange}
      >
        <Form.Item
          required
          label={t("easApplications.search.name.firstNameLabel")}
          name="firstName"
          className={styles["first-name"]}
          rules={[
            {
              required: true,
              whitespace: true,
              message: "First name is required"
            }
          ]}
        >
          <Input allowClear autoComplete="off" />
        </Form.Item>
        <Form.Item
          required
          label={t("easApplications.search.name.lastNameLabel")}
          name="familyName"
          className={styles["family-name"]}
          rules={[
            {
              required: true,
              message: "Family name is required",
              whitespace: true
            }
          ]}
        >
          <Input allowClear autoComplete="off" />
        </Form.Item>
        <Form.Item>
          <Button
            type="primary"
            block
            htmlType="submit"
            loading={isLoading}
            disabled={isLoading}
            style={{ marginTop: 8 }}
          >
            {t("easApplications.search.cta")}
          </Button>
        </Form.Item>
      </Form>
    </div>
  );
}
